import React, { useState } from "react";
import { Calendar, ChevronLeft, ChevronRight } from "lucide-react";

interface DateRangePickerProps {
  onApply: (startDate: Date | null, endDate: Date | null) => void;
  onCancel: () => void;
  initialStartDate?: Date | null;
  initialEndDate?: Date | null;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  onApply,
  onCancel,
  initialStartDate = null,
  initialEndDate = null,
}) => {
  const [startDate, setStartDate] = useState<Date | null>(initialStartDate);
  const [endDate, setEndDate] = useState<Date | null>(initialEndDate);
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [hoveredDate, setHoveredDate] = useState<Date | null>(null);

  // Keep local state in sync with props
  React.useEffect(() => {
    setStartDate(initialStartDate);
    setEndDate(initialEndDate);
  }, [initialStartDate, initialEndDate]);

  // Format date for display
  const formatDate = (date: Date | null) => {
    if (!date) return "";
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const generateCalendarDays = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    const days: (Date | null)[] = [];

    // Add previous month's days to fill the first week
    for (let i = 0; i < firstDay.getDay(); i++) {
      days.push(null);
    }

    // Add current month's days
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }

    return days;
  };
  const handleDateSelect = (day: Date | null) => {
    if (!day || isFutureDate(day)) return;

    if (!startDate || (startDate && endDate)) {
      setStartDate(day);
      setEndDate(null);
    } else if (startDate && !endDate) {
      if (day >= startDate) {
        setEndDate(day);
      } else {
        // Swap dates if selecting a date before the start date
        setEndDate(startDate);
        setStartDate(day);
      }
    }
  };
  const isDateInRange = (day: Date | null) => {
    if (!day) return false;
    if (!startDate) return false;

    // If we have both start and end dates, check if day is in range
    if (endDate) {
      return day >= startDate && day <= endDate;
    }

    // If we only have start date and are hovering, show preview range
    if (hoveredDate && hoveredDate >= startDate) {
      return day >= startDate && day <= hoveredDate;
    }

    return false;
  };

  const isStartOrEndDate = (day: Date | null) => {
    if (!day) return false;
    return (
      (startDate && day.toDateString() === startDate.toDateString()) ||
      (endDate && day.toDateString() === endDate.toDateString())
    );
  };

  const isStartDate = (day: Date | null) => {
    if (!day || !startDate) return false;
    return day.toDateString() === startDate.toDateString();
  };

  const isEndDate = (day: Date | null) => {
    if (!day || !endDate) return false;
    return day.toDateString() === endDate.toDateString();
  };
  const isCurrentDate = (day: Date | null) => {
    if (!day) return false;
    const today = new Date();
    return day.toDateString() === today.toDateString();
  };

  const isFutureDate = (day: Date | null) => {
    if (!day) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day
    const dayToCheck = new Date(day);
    dayToCheck.setHours(0, 0, 0, 0);
    return dayToCheck > today;
  };
  const changeMonth = (increment: number) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(currentMonth.getMonth() + increment);
    setCurrentMonth(newMonth);
  };

  const clearDates = () => {
    setStartDate(null);
    setEndDate(null);
    setHoveredDate(null);
  };

  const handleReset = () => {
    setStartDate(null);
    setEndDate(null);
    setHoveredDate(null);
  };

  const handleCancel = () => {
    setStartDate(null);
    setEndDate(null);
    setHoveredDate(null);
    onApply(initialStartDate ?? null, initialEndDate ?? null);
    onCancel();
  };

  const days = generateCalendarDays(currentMonth);

  return (
    <div className="w-[600px] h-[434px] bg-white border rounded-lg shadow-lg">
      {" "}
      {/* Date Range Input */}
      <div className="flex mb-4 justify-between items-center h-[29px] border border-[#F15D18] rounded-[4px] px-2 text-[14px] nunito-normal">
        <span
          className={startDate || endDate ? "text-[#404040]" : "text-[#C4C4C4]"}
        >
          {startDate && endDate
            ? `${formatDate(startDate)} - ${formatDate(endDate)}`
            : startDate
            ? `${formatDate(startDate)} - Select end date`
            : "Select date range"}
        </span>
        <div className="text-[#404040]">
          <Calendar className="w-[16px] h-[16px]" />
        </div>
      </div>
      {/* Calendars Container */}
      <div className="flex px-1  ">
        {/* First Calendar */}
        <div className="w-[300px] h-[340px] pr-2">
          {/* Month Navigation */}
          <div className="flex justify-between items-center ">
            <button
              onClick={() => changeMonth(-1)}
              className="text-[#404040]  p-1 hover:bg-gray-100 rounded"
            >
              <ChevronLeft className="w-[21px] h-[21px] text-[#404040]" />
            </button>
            <div className=" text-[#404040] text-[14px] Poppins-semibold ">
              {currentMonth.toLocaleString("default", { month: "long" })}{" "}
              {currentMonth.getFullYear()}
            </div>
            <button
              onClick={() => changeMonth(1)}
              className="text-gray-600 p-1 hover:bg-gray-100 rounded"
            >
              <ChevronRight className="w-[21px] h-[21px] text-[#404040]" />{" "}
            </button>
          </div>
          {/* Days of Week */}
          <div className="grid grid-cols-7 text-center text-[14px] bg-[#FFF9F6] h-[40px] items-center text-[#404040] mt-2 mb-1">
            {["S", "M", "T", "W", "T", "F", "S"].map((day, index) => (
              <div key={`first-${day}-${index}`}>{day}</div>
            ))}
          </div>{" "}
          {/* Calendar Days */}
          <div className="grid grid-cols-7   text-center   text-sm">
            {days.map((day, index) => (
              <div
                key={`first-calendar-${index}`}
                className={`h-[40px] w-[41.38px] flex justify-center items-center relative
                  ${!day ? "text-gray-300 items-center " : "cursor-pointer"}
                  ${isFutureDate(day) ? "text-gray-300 cursor-not-allowed" : ""}
                  ${isCurrentDate(day) ? "text-[#F15D18]" : "text-[#404040]"}
                  ${
                    isStartDate(day)
                      ? "bg-orange-500 text-white rounded-l-[8px]"
                      : ""
                  }
                  ${
                    isEndDate(day)
                      ? "border border-[#F15D18] rounded-r-[8px]"
                      : ""
                  }
                  ${
                    isDateInRange(day) && !isStartOrEndDate(day)
                      ? "bg-[#FEF0EA]"
                      : ""
                  }
                  ${
                    day &&
                    !isStartOrEndDate(day) &&
                    isDateInRange(day) &&
                    !isFutureDate(day)
                      ? "hover:bg-orange-200"
                      : day && !isFutureDate(day)
                      ? "hover:border hover:border-[#F15D18]"
                      : ""
                  }
                `}
                onClick={() => day && handleDateSelect(day)}
                onMouseEnter={() => day && setHoveredDate(day)}
                onMouseLeave={() => setHoveredDate(null)}
              >
                {day ? day.getDate() : ""}
                {isCurrentDate(day) && (
                  <div className="absolute bottom-1 w-1 h-1 bg-[#F15D18] rounded-full"></div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Second Calendar (Next Month) */}
        <div className="w-[300px] h-[340px] pl-2">
          {/* Month Navigation */}
          <div className="flex justify-between items-center mb-2">
            <button
              onClick={() => changeMonth(1)}
              className="text-gray-600 p-1 hover:bg-gray-100 rounded"
            >
              <ChevronLeft className="w-[21px] h-[21px] text-[#404040]" />
            </button>
            <div className=" text-[#404040] text-[14px] Poppins-semibold ">
              {new Date(
                currentMonth.getFullYear(),
                currentMonth.getMonth() + 1,
                1
              ).toLocaleString("default", { month: "long" })}{" "}
              {currentMonth.getFullYear()}
            </div>
            <button
              onClick={() => changeMonth(2)}
              className="text-gray-600 p-1 hover:bg-gray-100 rounded"
            >
              <ChevronRight className="w-[21px] h-[21px] text-[#404040]" />{" "}
            </button>
          </div>
          {/* Days of Week */}
          <div className="grid grid-cols-7 text-center text-[14px] bg-[#FFF9F6] h-[40px] items-center text-[#404040] mt-2 mb-1">
            {["S", "M", "T", "W", "T", "F", "S"].map((day, index) => (
              <div key={`second-${day}-${index}`}>{day}</div>
            ))}
          </div>{" "}
          {/* Calendar Days for Next Month */}
          <div className="grid grid-cols-7  text-center text-sm">
            {generateCalendarDays(
              new Date(
                currentMonth.getFullYear(),
                currentMonth.getMonth() + 1,
                1
              )
            ).map((day, index) => (
              <div
                key={`second-calendar-${index}`}
                className={`h-[40px] w-[41.38px] flex justify-center items-center relative
                  ${!day ? "text-gray-300" : "cursor-pointer"}
                  ${isFutureDate(day) ? "text-gray-300 cursor-not-allowed" : ""}
                  ${isCurrentDate(day) ? "text-[#F15D18]" : "text-[#404040]"}
                  ${
                    isStartDate(day)
                      ? "bg-[#F15D18] text-white rounded-l-[8px]"
                      : ""
                  }
                  ${
                    isEndDate(day)
                      ? "border border-[#F15D18] rounded-r-[8px]"
                      : ""
                  }
                  ${
                    isDateInRange(day) && !isStartOrEndDate(day)
                      ? "bg-[#FEF0EA]"
                      : ""
                  }
                  ${
                    day &&
                    !isStartOrEndDate(day) &&
                    isDateInRange(day) &&
                    !isFutureDate(day)
                      ? "hover:bg-orange-200"
                      : day && !isFutureDate(day)
                      ? "hover:border hover:border-[#F15D18]"
                      : ""
                  }
                `}
                onClick={() => day && handleDateSelect(day)}
                onMouseEnter={() => day && setHoveredDate(day)}
                onMouseLeave={() => setHoveredDate(null)}
              >
                {day ? day.getDate() : ""}
                {isCurrentDate(day) && (
                  <div className="absolute bottom-1 w-1 h-1 bg-[#F15D18] rounded-full"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="border-b-[1px] border border-[#FFF9F6] "></div>{" "}
      {/* Action Buttons */}
      <div className="flex justify-between px-2 items-center h-[45px] space-x-2">
        <div className="text-[#404040] text-[14px] nunito-regular flex-1">
          {startDate && endDate ? (
            <span>
              <span className="font-semibold">{formatDate(startDate)}</span>
              <span className="mx-2">to</span>
              <span className="font-semibold">{formatDate(endDate)}</span>
            </span>
          ) : startDate ? (
            <span>
              <span className="font-semibold">{formatDate(startDate)}</span>
              <span className="text-[#C4C4C4] ml-2">- Select end date</span>
            </span>
          ) : (
            <span className="text-[#C4C4C4]">Select start and end dates</span>
          )}
        </div>{" "}
        <div className="flex space-x-2">
          <button
            className="w-[67px] h-[29px] text-[#404040] text-[14px] hover:bg-gray-100 rounded"
            onClick={handleCancel}
          >
            Cancel
          </button>
          <button
            className={`w-[57px] h-[29px] text-[14px] text-white rounded transition-colors ${
              startDate && endDate
                ? "bg-[#F15D18] hover:bg-orange-600"
                : "bg-gray-300 cursor-not-allowed"
            }`}
            disabled={!startDate || !endDate}
            onClick={() => onApply(startDate, endDate)}
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

export default DateRangePicker;
